import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, AreaChart, Area,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const METRIC_CARDS = [
  { key: 'activeLoans', label: 'Active Loans', iconName: 'document', color: '#3a6ad9' },
  { key: 'totalVolume', label: 'Total Volume', iconName: 'currency', color: '#acf44e', format: 'currency' },
  { key: 'registeredUsers', label: 'Registered Users', iconName: 'users', color: '#3a6ad9' },
  { key: 'newThisMonth', label: 'New This Month', iconName: 'chart', color: '#ffdd86' },
  { key: 'approvedPartners', label: 'Approved Partners', iconName: 'users', color: '#fffd5b' },
  { key: 'openTasks', label: 'Open Tasks', iconName: 'check-circle', color: '#ff082f' },
  { key: 'avgNps', label: 'Avg NPS', iconName: 'star', color: '#ffdd86' },
  { key: 'conversionRate', label: 'Conversion Rate', iconName: 'target', color: '#acf44e', format: 'percent' },
];

function fmt(value, format) {
  if (format === 'currency') return '$' + (value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : value >= 1000 ? (value / 1000).toFixed(0) + 'K' : value);
  if (format === 'percent') return (value || 0).toFixed(1) + '%';
  return value ?? 0;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [metrics, setMetrics] = useState({});
  const [pipeline, setPipeline] = useState([]);
  const [urgent, setUrgent] = useState({ stuck: [], overdueTasks: [], failedVerifications: [] });
  const [activity, setActivity] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setError(null);
    const [mRes, pRes, uRes, aRes, ovRes] = await Promise.allSettled([
      axios.get(`${API}/api/admin/dashboard/metrics`, { headers }),
      axios.get(`${API}/api/admin/dashboard/pipeline`, { headers }),
      axios.get(`${API}/api/admin/dashboard/urgent`, { headers }),
      axios.get(`${API}/api/admin/dashboard/activity`, { headers }),
      axios.get(`${API}/api/admin/dashboard/overview-data`, { headers }),
    ]);
    if (mRes.status === 'fulfilled') setMetrics(mRes.value.data);
    else setError(`Metrics failed: ${mRes.reason?.response?.data?.error || mRes.reason?.message}`);
    if (pRes.status === 'fulfilled') setPipeline(pRes.value.data);
    if (uRes.status === 'fulfilled') setUrgent(uRes.value.data);
    if (aRes.status === 'fulfilled') setActivity(aRes.value.data);
    if (ovRes.status === 'fulfilled') setMonthlyData(ovRes.value.data?.months || []);
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
        <div style={{ color: '#9ca3af', fontSize: '14px' }}>Loading dashboard…</div>
      </div>
    </AdminLayout>
  );

  const urgentCount = (urgent.stuck?.length || 0) + (urgent.overdueTasks?.length || 0) + (urgent.failedVerifications?.length || 0);

  return (
    <AdminLayout>
      <div className="admin-mobile-pad" style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Dashboard</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Welcome back — here's what's happening today.</p>
        </div>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: '13px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Icon name="warning" size={14} color="#ff082f" /> {error}</span>
          </div>
        )}

        {/* Metric Cards 2×4 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {METRIC_CARDS.map((card) => (
            <div key={card.key} style={{
              backgroundColor: '#fff', borderRadius: '14px', padding: '18px 20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex', alignItems: 'center', gap: '16px',
              transition: 'box-shadow 0.15s',
            }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
            >
              <div style={{
                width: '46px', height: '46px', borderRadius: '12px',
                background: hexToRgba(card.color, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon name={card.iconName} size={22} color={card.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {card.label}
                </div>
                <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>
                  {fmt(metrics[card.key], card.format)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '28px' }}>
          {/* Pipeline by Phase */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Pipeline by Phase</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={pipeline} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="phase" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                <Bar dataKey="count" fill="#3a6ad9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Activity */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Monthly Applications</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={monthlyData.length > 0 ? monthlyData.map(m => ({ month: m.month, users: m.apps || 0 })) : [{ month: '—', users: 0 }]} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3a6ad9" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3a6ad9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                <Area type="monotone" dataKey="users" stroke="#3a6ad9" fill="url(#areaGrad)" strokeWidth={2} dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Active Pipeline Value */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Pipeline Value by Phase</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={pipeline} layout="vertical" margin={{ top: 0, right: 8, left: 40, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="phase" tick={{ fontSize: 10 }} width={60} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }} />
                <Bar dataKey="count" fill="#acf44e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Task Completion */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Tasks Overview</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              {[
                { label: 'Open Tasks', value: metrics.openTasks || 0, color: '#ff082f', max: Math.max(metrics.openTasks || 0, 1) },
                { label: 'Urgent Tasks', value: metrics.urgentTasks || 0, color: '#f59e0b', max: Math.max(metrics.openTasks || 0, 1) },
                { label: 'Active Pipeline', value: metrics.activePipeline || 0, color: '#3a6ad9', max: Math.max(metrics.registeredUsers || 0, 1) },
              ].map((item) => (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{item.label}</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{item.value}</span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (item.value / item.max) * 100)}%`, backgroundColor: item.color, borderRadius: '9999px', transition: 'width 0.5s' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row: Urgent + Activity */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {/* Urgent Alerts */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Urgent Alerts</h3>
              {urgentCount > 0 && <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px', backgroundColor: '#fff0f3', color: '#ff082f' }}>{urgentCount} items</span>}
            </div>
            <div style={{ padding: '12px 20px', maxHeight: '280px', overflowY: 'auto' }}>
              {urgent.stuck?.length > 0 && urgent.stuck.slice(0, 3).map((b) => (
                <div key={b.id} onClick={() => navigate(`/admin/borrowers/${b.id}`)}
                  style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', marginBottom: '8px', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#c2410c', display: 'flex', alignItems: 'center', gap: '5px' }}><Icon name="clock" size={12} color="#c2410c" /> Stuck Borrower</div>
                  <div style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>{b.first_name} {b.last_name}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>{b.days_stuck} days in "{b.application_status}"</div>
                </div>
              ))}
              {urgent.overdueTasks?.length > 0 && urgent.overdueTasks.slice(0, 3).map((t) => (
                <div key={t.id} onClick={() => navigate('/admin/tasks')}
                  style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', marginBottom: '8px', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#ff082f', display: 'flex', alignItems: 'center', gap: '5px' }}><Icon name="warning" size={12} color="#ff082f" /> Overdue Task</div>
                  <div style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>{t.title}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>Due {t.due_date}</div>
                </div>
              ))}
              {urgent.failedVerifications?.length > 0 && urgent.failedVerifications.slice(0, 3).map((v) => (
                <div key={v.user_id} onClick={() => navigate(`/admin/borrowers/${v.user_id}`)}
                  style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: '#edf1fb', border: '1px solid #c5d3f5', marginBottom: '8px', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#3a6ad9', display: 'flex', alignItems: 'center', gap: '5px' }}><Icon name="user" size={12} color="#3a6ad9" /> ID Review Needed</div>
                  <div style={{ fontSize: '13px', color: '#111827', marginTop: '2px' }}>{v.first_name} {v.last_name}</div>
                </div>
              ))}
              {urgentCount === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                  <div style={{ marginBottom: '8px' }}><Icon name="check-circle" size={24} color="#acf44e" /></div>
                  All clear — no urgent items
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Recent Activity</h3>
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {activity.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No recent activity</div>
              )}
              {activity.map((item, i) => (
                <div key={i} style={{ padding: '12px 20px', borderBottom: '1px solid #f9fafb', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#eff4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={item.type === 'signup' ? 'user' : 'document'} size={14} color="#3a6ad9" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.description}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{new Date(item.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
